import { ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { segmentTrackToggleInQueueTableFilter } from "in_queue/analytics";
import { useAdditionalColumnsForTable } from "in_queue/contexts/AdditionalColumnConfigsContext";
import { useCluster, useRegionConfig } from "in_queue/contexts/ClusterContext";
import {
    useAllQueueProjects,
    useProjectFuelTypes
} from "in_queue/contexts/ProjectDataContext";
import {
    useCurrentProjectId,
    useCurrentScenarioMetadata,
    useCurrentTrackedProject
} from "in_queue/contexts/ScenarioPageContext";
import { useScenarioResults } from "in_queue/contexts/ScenarioResultsContext";
import {
    getStudyGroupLabel,
    StudyCycle,
    StudyGroup,
    StudyPhase
} from "in_queue/types/clusterType";
import { InQueueRegionConfig } from "in_queue/types/configPerRegion";
import { ScenarioProject } from "in_queue/types/scenarioResultType";
import { MisoPercentageReduction } from "in_queue/types/scenarioType";
import {
    getHybridLimit,
    reduceHybridLimitByErisPercentage
} from "in_queue/types/scenarioUtils";
import { formatRank } from "in_queue/utils/rankFormatting";
import { keyBy, mapValues } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { Loading, mapLoadingState } from "types/loadingType";
import { RegionType } from "types/regionType";
import { spliceAdditionalColumns } from "./agGrid/additionalColumns";
import {
    AgGridColumnDefs,
    currencyFormatter,
    hideUnwantedColumns,
    powerFormatter,
    ScenarioProjectsTableColumnId,
    SCENARIO_TABLE_DEFAULT_COL_DEF
} from "./agGrid/agGridHelpers";
import { FuelTypeCellRenderer } from "./agGrid/cellRenderers";
import { BaseAgGrid } from "./base/BaseAgGrid";
import { RankCell } from "./RankPill";
import { ExportButton } from "./ribbon/ExportButton";
import { FilterSearchInput } from "./ribbon/FilterSearchInput";
import { ResultsCount } from "./ribbon/ResultsCount";
import { TableActionRibbon } from "./ribbon/TableActionRibbon";
import { ViewType, ViewTypesMenu } from "./ribbon/ViewTypesMenu";
import css from "./TableStyles.module.scss";

const SCENARIO_PROJECTS_ANCHOR_TO_COL_ID: Readonly<
    Record<string, ScenarioProjectsTableColumnId>
> = {
    project_id: "projectId",
    fuel_type: "fuelType",
    sum_allocated_cost: "sumAllocatedCost",
    num_shared_upgrades: "numSharedUpgrades",
    assigned_upgrades: "assignedUpgrades"
};

type ProjectStudyGroupMapping = { [projectId: string]: StudyGroup };
type ProjectMetadataMapping = {
    [projectId: string]: { [key: string]: string };
};
type ProjectFuelTypeMapping = { [projectId: string]: string };

const useProjectSubregions = (): Loading<ProjectStudyGroupMapping> => {
    const projects = useAllQueueProjects();
    return mapLoadingState(projects, (projects) => {
        return mapValues(
            keyBy(projects, "projectId"),
            (project) => project.cluster.studyGroup
        );
    });
};

const useProjectMetadatas = (): Loading<ProjectMetadataMapping> => {
    const projects = useAllQueueProjects();
    return mapLoadingState(projects, (projects) => {
        return mapValues(
            keyBy(projects, "projectId"),
            (project) => project.metadata
        );
    });
};

export const ScenarioProjectsTable: React.FC<{
    filterText: string;
    setFilterText: (value: string) => void;
    /**
     * Render all rows naturally, with no inner scrollbar, and pin the
     * column header to the top of the page scroll container so the
     * column names stay visible as the user scrolls.
     */
    stickyHeader?: boolean;
}> = ({ filterText, setFilterText, stickyHeader }) => {
    const {
        cluster: { studyGroup, studyCycle, studyPhase }
    } = useCurrentTrackedProject();
    const currentScenario = useCurrentScenarioMetadata();
    const { assumptions } = currentScenario;

    const gridRef = useRef<AgGridReact<ScenarioProject>>(null);

    const { region } = useCluster();
    const { scenarioResults, isLoadingRemaining } = useScenarioResults();
    const componentConfig = useRegionConfig();

    const projectSubregions = useProjectSubregions();
    const projectMetadatas = useProjectMetadatas();
    const projectFuelTypes = useProjectFuelTypes();
    const currentProjectId = useCurrentProjectId();
    const additionalColumnConfigs =
        useAdditionalColumnsForTable("scenario_projects");

    // TODO: remove this once we're able to configure columnsToRemove per cluster
    // instead of per region
    // https://linear.app/nira-energy/issue/NIRA-5109/configure-columnstoremove-per-cluster-instead-of-per-region
    const columnsToRemove = useMemo(() => {
        let columns = [
            ...componentConfig.scenarioProjectsTable.columnsToRemove
        ];
        if (
            (studyCycle === StudyCycle.MISO_2023 ||
                studyCycle === StudyCycle.MISO_2025) &&
            studyGroup === StudyGroup.DEFAULT_GROUP
        ) {
            columns = columns.concat([
                "subregion",
                "erisLpc",
                "erisComplex",
                "nrisComplex",
                "erisSnu"
            ]);

            // Make sure JTIQ is shown for 2023 since that's the only cycle it applies to
            if (studyCycle === StudyCycle.MISO_2023) {
                columns = columns.filter((col: string) => col !== "jtiq");
            }

            // We want to additionally hide NRIS costs for pre-screen, since we only have ERIS costs
            if (studyPhase === StudyPhase.PRESCREEN) {
                columns.push("nrisThermal");
            }
        }
        return columns;
    }, [
        componentConfig.scenarioProjectsTable.columnsToRemove,
        studyCycle,
        studyGroup
    ]);

    const columnDefs = useMemo(
        () =>
            spliceAdditionalColumns(
                getColumnDefs(
                    region,
                    currentProjectId,
                    projectSubregions,
                    projectMetadatas,
                    projectFuelTypes,
                    componentConfig,
                    columnsToRemove,
                    assumptions
                ),
                additionalColumnConfigs,
                SCENARIO_PROJECTS_ANCHOR_TO_COL_ID
            ),
        [
            region,
            currentProjectId,
            projectSubregions,
            projectMetadatas,
            projectFuelTypes,
            componentConfig,
            columnsToRemove,
            additionalColumnConfigs,
            assumptions
        ]
    );

    useEffect(() => {
        if (!columnsToRemove.includes("subregion")) {
            if (gridRef && gridRef.current && gridRef.current.api) {
                gridRef.current.api.setFilterModel({
                    subregion: {
                        filterType: "text",
                        type: "contains",
                        filter: studyGroup.toLowerCase()
                    }
                });
                gridRef.current.api.onFilterChanged();
            }
        }
    }, [gridRef?.current?.api]);

    const [viewType, setViewType] = useState<ViewType>("related");
    const [visibleCount, setVisibleCount] = useState<number | undefined>(
        undefined
    );

    const projects = mapLoadingState(scenarioResults, (d) => d.projects);
    const totalCount =
        projects !== "loading" && !isLoadingRemaining
            ? projects.length
            : undefined;

    const leftActions = [
        <ViewTypesMenu
            currentViewType={viewType}
            setCurrentViewType={(newViewType: ViewType) => {
                segmentTrackToggleInQueueTableFilter({
                    tableName: "Projects",
                    filterName: newViewType
                });
                setViewType(newViewType);
            }}
            validViewTypes={["related", "all"]}
        />
    ];
    const rightActions = [
        <ExportButton gridRef={gridRef} exportName="Projects" />,
        <FilterSearchInput value={filterText} onChange={setFilterText} />
    ];
    return (
        <div className={css["table-ribbon-wrapper"]}>
            <TableActionRibbon
                leftActions={leftActions}
                rightActions={rightActions}
                sticky={stickyHeader}
            />
            <BaseAgGrid<ScenarioProject>
                ref={gridRef}
                loadingData={projects}
                columnDefs={columnDefs}
                analyticsTableName="Projects"
                stickyHeader={stickyHeader}
                defaultColDef={SCENARIO_TABLE_DEFAULT_COL_DEF}
                rowHeight={52}
                quickFilterText={filterText}
                onModelUpdated={(event) =>
                    setVisibleCount(event.api.getDisplayedRowCount())
                }
                isExternalFilterPresent={() => viewType !== "all"}
                doesExternalFilterPass={(node) => {
                    if (
                        viewType !== "related" ||
                        scenarioResults === "loading"
                    ) {
                        return true;
                    } else {
                        const relatedProjectIds =
                            scenarioResults.relatedProjectIds;
                        return (
                            node.data != null &&
                            relatedProjectIds.has(node.data.projectId)
                        );
                    }
                }}
            />
            <div className={css["table-footer"]}>
                <ResultsCount visible={visibleCount} total={totalCount} />
            </div>
        </div>
    );
};

const getColumnDefs = (
    region: RegionType,
    currentProjectId: string,
    projectSubregions: Loading<ProjectStudyGroupMapping>,
    projectMetadatas: Loading<ProjectMetadataMapping>,
    projectFuelTypes: Loading<ProjectFuelTypeMapping>,
    config: InQueueRegionConfig,
    columnsToRemove: string[],
    assumptions: Record<string, MisoPercentageReduction>
) => {
    const columnDefs: AgGridColumnDefs<
        ScenarioProject,
        ScenarioProjectsTableColumnId
    > = [
        {
            field: "projectId",
            headerName: "Project",
            width: 125,
            pinned: true,
            filter: true,
            colId: "projectId"
        },
        {
            headerName: "Subregion",
            colId: "subregion",
            width: 125,
            pinned: true,
            filter: true,
            valueGetter: ({ data }) => {
                if (
                    !data ||
                    projectSubregions === "loading" ||
                    !projectSubregions[data.projectId]
                ) {
                    return "-";
                }
                return getStudyGroupLabel(
                    region,
                    projectSubregions[data.projectId]
                );
            }
        },
        {
            field: "numSharedUpgrades",
            headerName: "# shared upgrades",
            headerTooltip: `The number of network upgrades common between each project and ${currentProjectId}.`,
            wrapHeaderText: true,
            type: "numericColumn",
            width: 80,
            colId: "numSharedUpgrades"
        },
        {
            field: "costPerMw",
            headerName: "$ / MW",
            sort: "desc",
            type: "numericColumn",
            width: 100,
            valueFormatter: currencyFormatter,
            colId: "costPerMW"
        },
        {
            colId: "rank",
            headerName: "Rank",
            headerTooltip:
                "Project rank by $/MW in this scenario (1 = cheapest). Percentile = rank / total.",
            type: "numericColumn",
            width: 90,
            cellRenderer: ({ data }: ICellRendererParams<ScenarioProject>) => (
                <RankCell
                    rank={data?.rank}
                    totalRanked={data?.totalRanked}
                    percentile={data?.rankPercentile}
                />
            ),
            useValueFormatterForExport: true,
            // RankPill carries its own tooltip; the grid's default would
            // stack a second one on the same cell.
            tooltipValueGetter: () => undefined,
            valueGetter: ({ data }) => data?.rank ?? null,
            valueFormatter: ({ value, data }) => {
                if (value === null || value === undefined || !data) {
                    return "-";
                }
                if (
                    data.rank === undefined ||
                    data.rankPercentile === undefined ||
                    !data.totalRanked
                ) {
                    return `${value}`;
                }
                return formatRank(
                    data.rank,
                    data.totalRanked,
                    data.rankPercentile
                );
            },
            comparator: (a: number | null, b: number | null) => {
                if (a === null && b === null) return 0;
                if (a === null) return 1;
                if (b === null) return -1;
                return a - b;
            }
        },
        {
            field: "sumAllocatedCost",
            headerName: "Allocated cost",
            type: "numericColumn",
            width: 140,
            valueFormatter: currencyFormatter,
            colId: "sumAllocatedCost"
        },
        {
            headerName: "Size (MW)",
            children: [
                {
                    headerName: config.sizeLabel.energyLabel,
                    width: 80,
                    colId: "erisSize",
                    type: "numericColumn",
                    valueFormatter: powerFormatter,
                    valueGetter: ({ data }) => {
                        if (!data || projectMetadatas === "loading") {
                            return null;
                        } else {
                            return getHybridLimit(
                                data,
                                projectMetadatas[data.projectId],
                                true
                            );
                        }
                    }
                },
                {
                    headerName: config.sizeLabel.capacityLabel,
                    width: 80,
                    colId: "nrisSize",
                    type: "numericColumn",
                    valueFormatter: powerFormatter,
                    valueGetter: ({ data }) => {
                        if (!data || projectMetadatas === "loading") {
                            return null;
                        } else {
                            return getHybridLimit(
                                data,
                                projectMetadatas[data.projectId]
                            );
                        }
                    }
                },
                {
                    headerName: "Hybrid Limit",
                    colId: "hybridLimit",
                    type: "numericColumn",
                    headerTooltip:
                        "SPP's published POI cap for this hybrid project. In scenarios with a percentage downsize, this value is scaled by the scenario's ERIS reduction to reflect the effective POI injection used in cost allocation (matches `mw_impact = hybrid_limit × (1 − ERIS reduction) × DFAX`).",
                    width: 80,
                    valueFormatter: powerFormatter,
                    valueGetter: ({ data }) => {
                        if (
                            !data ||
                            projectMetadatas === "loading" ||
                            !projectMetadatas[data.projectId] ||
                            !projectMetadatas[data.projectId]["Hybrid Limit"]
                        ) {
                            return null;
                        }
                        const baseHybridLimit =
                            +projectMetadatas[data.projectId]["Hybrid Limit"];
                        if (baseHybridLimit === 0) {
                            return null;
                        }
                        // If the scenario applies a percentage reduction to
                        // this project, scale the Hybrid Limit by the ERIS
                        // reduction so the UI matches the cost-allocation
                        // pipeline (see reduceHybridLimitByErisPercentage).
                        const assumption = assumptions[data.projectId];
                        if (!assumption) {
                            return baseHybridLimit;
                        }
                        return reduceHybridLimitByErisPercentage(
                            baseHybridLimit,
                            assumption
                        );
                    }
                }
            ]
        },
        {
            headerName: "JTIQ Participation Group",
            colId: "jtiq",
            width: 160,
            filter: true,
            wrapHeaderText: true,
            valueGetter: ({ data }) => {
                if (!data || !Object.keys(data.metadata).includes("jtiq")) {
                    return null;
                }
                return data.metadata["jtiq"] ? "Yes" : "No";
            }
        },
        {
            headerName: "Cost by type",
            children: [
                {
                    field: "sumAllocatedCostByConstraintType.ERIS",
                    headerName: "ERIS Thermal",
                    type: "numericColumn",
                    width: 120,
                    valueFormatter: currencyFormatter,
                    colId: "erisThermal"
                },
                {
                    field: "sumAllocatedCostByConstraintType.ERIS_LPC",
                    headerName: "ERIS LPC",
                    type: "numericColumn",
                    width: 120,
                    valueFormatter: currencyFormatter,
                    colId: "erisLpc"
                },
                {
                    field: "sumAllocatedCostByConstraintType.ERIS_COMPLEX",
                    headerName: "ERIS Complex",
                    type: "numericColumn",
                    width: 120,
                    valueFormatter: currencyFormatter,
                    colId: "erisComplex"
                },
                {
                    field: "sumAllocatedCostByConstraintType.NRIS",
                    headerName: "NRIS Thermal",
                    type: "numericColumn",
                    width: 120,
                    valueFormatter: currencyFormatter,
                    colId: "nrisThermal"
                },
                {
                    field: "sumAllocatedCostByConstraintType.NRIS_COMPLEX",
                    headerName: "NRIS Complex",
                    type: "numericColumn",
                    width: 120,
                    valueFormatter: currencyFormatter,
                    colId: "nrisComplex"
                },
                {
                    field: "sumAllocatedCostByConstraintType.SHARED_NETWORK_UPGRADE",
                    headerName: "ERIS SNU",
                    type: "numericColumn",
                    width: 120,
                    valueFormatter: currencyFormatter,
                    colId: "erisSnu"
                }
            ]
        },
        {
            field: "assignedUpgrades",
            headerName: "All assigned upgrades",
            minWidth: 400,
            flex: 1,
            colId: "assignedUpgrades"
        },
        {
            headerName: "Group",
            colId: "group",
            width: 80,
            pinned: true,
            filter: true,
            valueGetter: ({ data }) => {
                if (
                    !data ||
                    projectMetadatas === "loading" ||
                    !projectMetadatas[data.projectId] ||
                    !projectMetadatas[data.projectId]["Group"]
                ) {
                    return null;
                }
                return projectMetadatas[data.projectId]["Group"];
            }
        },
        {
            headerName: "Fuel Type",
            colId: "fuelType",
            width: 150,
            pinned: true,
            filter: true,
            cellRenderer: FuelTypeCellRenderer,
            valueGetter: ({ data }) => {
                if (
                    !data ||
                    projectFuelTypes === "loading" ||
                    !projectFuelTypes[data.projectId]
                ) {
                    return null;
                }
                return projectFuelTypes[data.projectId];
            }
        }
    ];

    hideUnwantedColumns(columnDefs, columnsToRemove);

    return columnDefs;
};
